import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ZoomIn, ZoomOut, Maximize2, Move,
  Camera, DoorOpen, Bell, Lock, Radio, HelpCircle, X, MapPin, Edit2,
} from 'lucide-react';
import useMapStore from '../stores/mapStore';
import './MapDetail.css';

const DEVICE_TYPE_CONFIG = {
  CAMERA:   { icon: Camera,    color: '#3b82f6', label: 'Camera'   },
  DOOR:     { icon: DoorOpen,  color: '#10b981', label: 'Door'     },
  INTERCOM: { icon: Bell,      color: '#f59e0b', label: 'Intercom' },
  LOCK:     { icon: Lock,      color: '#8b5cf6', label: 'Lock'     },
  SENSOR:   { icon: Radio,     color: '#ec4899', label: 'Sensor'   },
  OTHER:    { icon: HelpCircle,color: '#6b7280', label: 'Device'   },
};

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.15;

export default function SurveyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchFloorPlanById, loading } = useMapStore();

  const [floorPlan, setFloorPlan] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef(null);

  useEffect(() => {
    const main = document.querySelector(".main-content");
    if (main) {
      main.style.overflow = "hidden";
      main.style.padding = "0";
    }
    return () => {
      if (main) {
        main.style.overflow = "";
        main.style.padding = "";
      }
    };
  }, []);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchFloorPlanById(id);
      if (!cancelled && data) {
        setFloorPlan(data);
        console.log("[MapDetail] imageUrl:", data.imageUrl);
        setDevices(data.deviceLocations || []);
        setTimeout(() => fitToView(), 100);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const fitToView = useCallback(() => {
    if (!containerRef.current || !imageRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const img = imageRef.current;
    const naturalW = img.naturalWidth || img.width || 800;
    const naturalH = img.naturalHeight || img.height || 600;
    const topbar = document.querySelector(".survey-detail-topbar");
    const topbarH = topbar ? topbar.offsetHeight : 56;
    const scaleX = (container.width - 40) / naturalW;
    const scaleY = (container.height - 80) / naturalH;
    const newZoom = Math.min(scaleX, scaleY) * 0.80;
    setZoom(newZoom);
    setPan({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };
  const onMouseMove = (e) => {
    if (!isPanning || !panStart.current) return;
    setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
  };
  const onMouseUp = () => { setIsPanning(false); panStart.current = null; };

  const zoomIn  = () => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));

  if (loading && !floorPlan) {
    return (
      <div className="survey-detail-loading">
        <div className="spinner" />
        <span>Loading floor plan…</span>
      </div>
    );
  }

  if (!floorPlan) {
    return (
      <div className="survey-detail-loading">
        <MapPin size={32} />
        <span>Floor plan not found.</span>
        <button className="btn-secondary" onClick={() => navigate('/map')}>Back to Maps</button>
      </div>
    );
  }

  const sourceLabel = floorPlan.sourceType === 'satellite' ? 'Satellite' : 'Imported';
  const sourceBadgeClass = floorPlan.sourceType === 'satellite' ? 'badge-satellite' : 'badge-imported';

  return (
    <div className="survey-detail-root">
      <div className="survey-detail-topbar">
        <button className="topbar-back" onClick={() => navigate('/map')}>
          <ArrowLeft size={18} />
          <span>Site Maps</span>
        </button>
        <div className="topbar-title">
          <span className="topbar-map-name">{floorPlan.surveyLabel || floorPlan.name || 'Unnamed Map'}</span>
          {floorPlan.buildingName && <span className="topbar-building">{floorPlan.buildingName}</span>}
          {floorPlan.floorNumber != null && floorPlan.floorNumber !== 0 && <span className="topbar-floor">Floor {floorPlan.floorNumber}</span>}
          <span className={`source-badge ${sourceBadgeClass}`}>{sourceLabel}</span>
        </div>
        <div className="topbar-actions">
          <span className="device-count-label">
            {devices.length} device{devices.length !== 1 ? 's' : ''} placed
          </span>
          <button className="btn-primary btn-sm" onClick={() => navigate(`/map/survey/${id}/place`)}>
            <Edit2 size={14} />
            Place Devices
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className={`survey-detail-canvas ${isPanning ? 'is-panning' : ''}`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onClick={() => setSelectedDevice(null)}
      >
        <div
          className="canvas-transform-layer"
          style={{ position: "absolute", top: "50%", left: "50%", transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom})`, transformOrigin: "center center" }}
        >
          <div className="floor-plan-image-wrapper">
            <img
              ref={imageRef}
              src={floorPlan.imageUrl}
              alt={floorPlan.surveyLabel}
              draggable={false}
              onLoad={fitToView}
              onError={(e) => console.error("Image failed to load:", e.target.src)}
              className="floor-plan-image"
            />
            {devices.map((device) => {
              if (device.floorX == null || device.floorY == null) return null;
              const cfg = DEVICE_TYPE_CONFIG[device.deviceType] || DEVICE_TYPE_CONFIG.OTHER;
              const Icon = cfg.icon;
              const isSelected = selectedDevice?.id === device.id;
              const isCamera = device.deviceType === 'CAMERA';
              return (
                <div key={device.id} style={{ position: 'absolute', left: `${device.floorX}%`, top: `${device.floorY}%`, width: 0, height: 0, overflow: 'visible', zIndex: isSelected ? 20 : 5 }}>
                  {isCamera && <FovCone rotation={device.rotation || 0} fovAngle={device.fovAngle ?? 90} fovRange={device.fovRange ?? 70} color={cfg.color} />}
                  <button
                    className={`device-marker ${isSelected ? 'device-marker--selected' : ''}`}
                    style={{
                      position: 'absolute',
                      transform: 'translate(-50%, -50%)',
                      '--marker-color': cfg.color,
                    }}
                    onClick={(e) => { e.stopPropagation(); setSelectedDevice(isSelected ? null : device); }}
                    title={device.deviceName || device.label || cfg.label}
                  >
                    <Icon size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="zoom-controls">
          <button className="zoom-btn" onClick={zoomIn}  title="Zoom in"><ZoomIn  size={16} style={{ width: 16, height: 16, flexShrink: 0 }} /></button>
          <span className="zoom-level">{Math.round(zoom * 100)}%</span>
          <button className="zoom-btn" onClick={zoomOut} title="Zoom out"><ZoomOut size={16} style={{ width: 16, height: 16, flexShrink: 0 }} /></button>
          <div className="zoom-divider" />
          <button className="zoom-btn" onClick={fitToView} title="Fit to view"><Maximize2 size={16} style={{ width: 16, height: 16, flexShrink: 0 }} /></button>
        </div>

        {devices.length === 0 && (
          <div className="canvas-empty-hint">
            <Move size={20} />
            <span>No devices placed yet — click <strong>Place Devices</strong> to get started</span>
          </div>
        )}
      </div>

      {selectedDevice && (
        <DeviceInfoPanel device={selectedDevice} onClose={() => setSelectedDevice(null)} />
      )}
    </div>
  );
}

function DeviceInfoPanel({ device, onClose }) {
  const cfg = DEVICE_TYPE_CONFIG[device.deviceType] || DEVICE_TYPE_CONFIG.OTHER;
  const Icon = cfg.icon;
  return (
    <div className="device-info-panel">
      <div className="dip-header">
        <div className="dip-icon" style={{ '--marker-color': cfg.color }}>
          <Icon size={20} />
        </div>
        <div className="dip-title">
          <span className="dip-name">{device.deviceName || device.label || 'Unnamed Device'}</span>
          <span className="dip-type">{cfg.label}</span>
        </div>
        <button className="dip-close" onClick={onClose}><X size={16} /></button>
      </div>
      <div className="dip-body">
        {device.label && device.label !== device.deviceName && <InfoRow label="Label" value={device.label} />}
        {device.integrationId && <InfoRow label="Integration ID" value={device.integrationId} />}
        {device.deviceId     && <InfoRow label="Device ID"      value={device.deviceId} />}
        {device.rotation != null && <InfoRow label="Rotation" value={`${device.rotation}°`} />}
        {device.deviceType === 'CAMERA' && device.fovAngle != null && <InfoRow label="FOV" value={`${device.fovAngle}°`} />}
        {device.notes        && <InfoRow label="Notes"          value={device.notes} />}
        {device.placedAt     && <InfoRow label="Placed" value={new Date(device.placedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />}
        <InfoRow label="Position" value={`X: ${device.floorX?.toFixed(1)}%  Y: ${device.floorY?.toFixed(1)}%`} />
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="dip-row">
      <span className="dip-row-label">{label}</span>
      <span className="dip-row-value">{value}</span>
    </div>
  );
}

function FovCone({ rotation, fovAngle, fovRange, color }) {
  const r = fovRange ?? 70;
  const half = fovAngle / 2;
  // Build polygon points for clip-path: apex at center-bottom of box,
  // fan upward. We use a 200x200 box, apex at (50%, 50%).
  const steps = 24;
  const points = [`50% 50%`];
  for (let i = 0; i <= steps; i++) {
    const angle = (-half + (fovAngle * i / steps)) * (Math.PI / 180);
    const px = 50 + 50 * Math.sin(angle);
    const py = 50 - 50 * Math.cos(angle);
    points.push(`${px.toFixed(2)}% ${py.toFixed(2)}%`);
  }
  const clipPath = `polygon(${points.join(', ')})`;
  const size = r * 2;
  return (
    <div style={{
      position: 'absolute',
      left: `-${r}px`,
      top: `-${r}px`,
      width: `${size}px`,
      height: `${size}px`,
      background: color,
      opacity: 0.3,
      clipPath,
      transform: `rotate(${rotation}deg)`,
      transformOrigin: `${r}px ${r}px`,
      pointerEvents: 'none',
      zIndex: 0,
      borderRadius: 0,
    }} />
  );
}
